import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiUrl } from '../config/api';
import { getAuthToken } from '../utils/authUtils';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import ToolState from './mmec/ToolState';

/**
 * Posted Calls — deduplicated, tracked board. One call per (underlying, direction);
 * the backend dedup gate merges the pulse firehose and cross-source repeats into one
 * row, and a periodic job prices each open call's underlying for a directional result.
 * Three views: Board (tracked calls), Performance (win rate / by source), Review queue
 * (gated sources awaiting approval). Quiet — no popups.
 */

interface Call {
  id: string;
  underlying: string;
  direction: 'bullish' | 'bearish' | string;
  status: string;
  moderation: string;
  gated: boolean;
  sourceLabel: string;
  platformSource: string;
  sources: string[];
  seenCount: number;
  cycle: number;
  confidence?: number | null;
  strategy?: string | null;
  contract?: string | null;
  target?: string | null;
  stop?: string | null;
  rationale?: string | null;
  horizon?: string | null;
  section?: string | null;
  ev?: number | null;
  probTouch?: number | null;
  dte?: number | null;
  relVol?: number | null;
  nAppearances?: number | null;
  entry?: number | null;
  current?: number | null;
  pnlPct?: number | null;
  outcome?: string | null;
  resultPct?: number | null;
  openedAt?: string | null;
  closedAt?: string | null;
  ts?: string | null;
}

interface Stats {
  gate?: string;
  totals: { calls: number; open: number; closed: number; winRate: number | null };
  bySource: Record<string, { total: number; open: number; closed: number; wins: number; avgResult: number | null }>;
}

const SOURCE_COLOR: Record<string, string> = {
  sovereign_pulse: '#5b8def', agent: '#2dd4bf', opp_stream: '#a78bfa', trade_portal: '#f4a261', manual: '#94a3b8',
};
const STATUS_COLOR: Record<string, string> = {
  OPEN: '#5b8def', TARGET_HIT: '#2eb872', STOP_LOSS: '#e5534b', EXPIRED: '#8a8f98', CLOSED: '#64748b', PENDING: '#f4a261',
};
const STATUS_FILTERS = [['', 'All'], ['OPEN', 'Open'], ['EXPIRED', 'Expired'], ['CLOSED', 'Closed']] as const;

async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json', ...(init.headers as Record<string, string>) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(getApiUrl(path), { ...init, headers });
}

const pct = (v?: number | null) => (v == null ? '' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);
const money = (v?: number | null) => (v == null ? '—' : v >= 1000 ? v.toFixed(0) : v.toFixed(2));

const S = {
  wrap: { padding: '18px 20px 48px', maxWidth: 1060, margin: '0 auto', color: '#e8eaed' } as React.CSSProperties,
  h1: { fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' } as React.CSSProperties,
  sub: { color: '#9aa0aa', fontSize: 13, margin: '4px 0 0' } as React.CSSProperties,
  seg: { display: 'inline-flex', gap: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 9, padding: 3, marginTop: 14 } as React.CSSProperties,
  segBtn: (on: boolean): React.CSSProperties => ({ border: 'none', cursor: 'pointer', padding: '6px 15px', borderRadius: 7, fontSize: 13, fontWeight: 600, background: on ? '#1f6feb' : 'transparent', color: on ? '#fff' : '#aab1bd' }),
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8, margin: '14px 0 2px', alignItems: 'center' } as React.CSSProperties,
  chip: (on: boolean, color?: string): React.CSSProperties => ({ border: `1px solid ${on ? (color || '#1f6feb') : 'rgba(255,255,255,0.12)'}`, background: on ? `${color || '#1f6feb'}22` : 'transparent', color: on ? '#fff' : '#aab1bd', borderRadius: 999, padding: '5px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }),
  input: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#e8eaed', padding: '7px 11px', fontSize: 13, width: 130 } as React.CSSProperties,
  card: (color: string): React.CSSProperties => ({ display: 'flex', gap: 16, alignItems: 'center', background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)', borderLeft: `3px solid ${color}`, borderRadius: 10, padding: '12px 16px', marginBottom: 9 }),
  badge: (color: string): React.CSSProperties => ({ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.03em', color, background: `${color}1e`, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 7px' }),
  pill: (color: string): React.CSSProperties => ({ fontSize: 10.5, fontWeight: 700, color, background: `${color}1e`, borderRadius: 6, padding: '2px 7px' }),
  bar: { position: 'relative', height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden', marginTop: 7 } as React.CSSProperties,
  statCard: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '16px 18px', minWidth: 130 } as React.CSSProperties,
  big: { fontSize: 26, fontWeight: 700 } as React.CSSProperties,
  lbl: { color: '#8a8f98', fontSize: 11.5, marginTop: 2 } as React.CSSProperties,
  th: { textAlign: 'left' as const, color: '#8a8f98', fontSize: 11.5, fontWeight: 600, padding: '7px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  td: { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.05)' } as React.CSSProperties,
  btnA: { border: '1px solid #2eb87255', background: '#2eb87222', color: '#54d08a', borderRadius: 7, padding: '6px 13px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' } as React.CSSProperties,
  btnR: { border: '1px solid #e5534b55', background: '#e5534b22', color: '#ef7a73', borderRadius: 7, padding: '6px 13px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' } as React.CSSProperties,
  empty: { textAlign: 'center' as const, color: '#7f868f', padding: '44px 0', fontSize: 14 },
};

const PostedCallsFeed: React.FC = () => {
  const [view, setView] = useState<'board' | 'held' | 'performance' | 'review'>('board');
  const [calls, setCalls] = useState<Call[]>([]);
  const [held, setHeld] = useState<Call[]>([]);
  const [pending, setPending] = useState<Call[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (sourceFilter) qs.set('source', sourceFilter);
      if (statusFilter) qs.set('status', statusFilter);
      if (symbol.trim()) qs.set('symbol', symbol.trim().toUpperCase());
      qs.set('limit', '400');
      const [f, h, p, s] = await Promise.all([
        authFetch(`/api/calls/feed?${qs.toString()}`),
        authFetch(`/api/calls/held?${qs.toString()}`),
        authFetch('/api/calls/pending'),
        authFetch('/api/calls/stats'),
      ]);
      const fj = await f.json();
      if (!fj.success) throw new Error(fj.error || 'feed error');
      setCalls(fj.calls || []);
      setHeld((await h.json()).calls || []);
      setPending((await p.json()).calls || []);
      setStats(await s.json());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, statusFilter, symbol]);

  useEffect(() => {
    setLoading(true);
    load();
    const id = window.setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const moderate = useCallback(async (id: string, decision: 'approve' | 'reject') => {
    setBusyId(id);
    try {
      await authFetch(`/api/calls/${encodeURIComponent(id)}/moderate`, { method: 'POST', body: JSON.stringify({ decision }) });
      await load();
    } finally { setBusyId(null); }
  }, [load]);

  const activeList = view === 'held' ? held : calls;
  const sourceChips = useMemo(() => {
    const m: Record<string, string> = { sovereign_pulse: 'Sovereign Pulse', agent: 'Agent', opp_stream: 'Opp Stream', trade_portal: 'Trade Portal', manual: 'Manual' };
    const present = Array.from(new Set(activeList.map((c) => c.platformSource)));
    return present.map((k) => ({ key: k, label: m[k] || k, color: SOURCE_COLOR[k] || '#94a3b8' }));
  }, [activeList]);

  const renderCard = (c: Call) => {
    const color = SOURCE_COLOR[c.platformSource] || '#94a3b8';
    const statusColor = STATUS_COLOR[c.status] || '#8a8f98';
    const up = c.direction === 'bullish';
    const dirColor = up ? '#2eb872' : '#e5534b';
    const pnl = c.pnlPct;
    const pnlColor = pnl == null ? '#8a8f98' : pnl >= 0 ? '#54d08a' : '#ef7a73';
    const prog = Math.max(0, Math.min(100, 50 + (pnl ?? 0) * 2.5)); // -20%..+20% → 0..100
    return (
      <div key={c.id} style={S.card(color)}>
        <div style={{ minWidth: 92 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>{c.underlying}</span>
            <span style={{ color: dirColor, fontSize: 13, fontWeight: 700 }}>{up ? '▲' : '▼'}</span>
          </div>
          <div style={{ color: '#8a8f98', fontSize: 11, marginTop: 2 }}>{up ? 'Bullish' : 'Bearish'}</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {c.sources.map((s) => <span key={s} style={S.badge(color)}>{s}</span>)}
            {c.seenCount > 1 ? <span style={{ color: '#7f868f', fontSize: 11 }}>×{c.seenCount}</span> : null}
            <span style={S.pill(statusColor)}>{(c.status || '').replace('_', ' ')}</span>
            {c.confidence != null ? <span style={{ color: '#8a8f98', fontSize: 11.5 }}>conf {Math.round(Number(c.confidence))}{Number(c.confidence) <= 1 ? '' : '%'}</span> : null}
          </div>
          <div style={S.bar}>
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.25)' }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, background: pnlColor, left: pnl != null && pnl < 0 ? `${prog}%` : '50%', width: `${Math.abs(prog - 50)}%` }} />
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 7, fontSize: 12, color: '#c4c9d2', flexWrap: 'wrap' }}>
            <span><span style={{ color: '#7f868f' }}>Entry </span>{money(c.entry)}</span>
            <span><span style={{ color: '#7f868f' }}>Now </span>{money(c.current)}</span>
            {c.section ? <span><span style={{ color: '#7f868f' }}>Desk </span>{c.section}</span> : null}
            {c.ev != null ? <span style={{ color: c.ev >= 0 ? '#54d08a' : '#b98' }}><span style={{ color: '#7f868f' }}>EV </span>{c.ev >= 0 ? '+' : '−'}${Math.abs(c.ev) >= 1000 ? (Math.abs(c.ev) / 1000).toFixed(0) + 'k' : Math.abs(c.ev).toFixed(0)}</span> : null}
            {c.dte != null ? <span><span style={{ color: '#7f868f' }}>DTE </span>{Math.round(Number(c.dte))}</span> : null}
            {c.relVol != null && c.relVol >= 1 ? <span><span style={{ color: '#7f868f' }}>relVol </span>{Number(c.relVol).toFixed(1)}×</span> : null}
            {c.horizon ? <span><span style={{ color: '#7f868f' }}>Horizon </span>{String(c.horizon).slice(0, 10)}</span> : null}
          </div>
        </div>
        <div style={{ textAlign: 'right', minWidth: 74 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: pnlColor }}>{pnl == null ? '—' : pct(pnl)}</div>
          <div style={{ color: '#7f868f', fontSize: 10.5 }}>{c.status === 'OPEN' ? 'unrealized' : 'result'}</div>
          {view === 'review' || view === 'held' ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
              <button style={S.btnA} disabled={busyId === c.id} onClick={() => moderate(c.id, 'approve')}>Approve</button>
              <button style={S.btnR} disabled={busyId === c.id} onClick={() => moderate(c.id, 'reject')}>Reject</button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderPerformance = () => {
    if (!stats) return <div style={S.empty}>No stats yet.</div>;
    const t = stats.totals;
    return (
      <div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0 20px' }}>
          <div style={S.statCard}><div style={S.big}>{t.calls}</div><div style={S.lbl}>Tracked calls</div></div>
          <div style={S.statCard}><div style={S.big}>{t.open}</div><div style={S.lbl}>Open</div></div>
          <div style={S.statCard}><div style={S.big}>{t.closed}</div><div style={S.lbl}>Closed</div></div>
          <div style={S.statCard}><div style={{ ...S.big, color: t.winRate == null ? '#e8eaed' : t.winRate >= 50 ? '#54d08a' : '#ef7a73' }}>{t.winRate == null ? '—' : `${t.winRate}%`}</div><div style={S.lbl}>Win rate (closed)</div></div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={S.th}>Source</th><th style={S.th}>Total</th><th style={S.th}>Open</th><th style={S.th}>Closed</th><th style={S.th}>Wins</th><th style={S.th}>Avg result</th></tr></thead>
          <tbody>
            {Object.entries(stats.bySource).map(([src, b]) => (
              <tr key={src}>
                <td style={S.td}>{src}</td>
                <td style={S.td}>{b.total}</td>
                <td style={S.td}>{b.open}</td>
                <td style={S.td}>{b.closed}</td>
                <td style={S.td}>{b.wins}</td>
                <td style={{ ...S.td, color: b.avgResult == null ? '#8a8f98' : b.avgResult >= 0 ? '#54d08a' : '#ef7a73' }}>{b.avgResult == null ? '—' : pct(b.avgResult)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const list = view === 'review' ? pending : view === 'held' ? held : calls;

  // ── Design System preview (artboard web/posted-calls): pills row on top, then one table. ──
  //    Only rendered under html[data-mmec-ds="1"]; members keep the card board above.
  if (isDesignSystemOn()) {
    const STATUS_TONE: Record<string, string> = { TARGET_HIT: 'gold', STOP_LOSS: 'down', OPEN: 'up' };
    const postedAt = (c: Call) => {
      const raw = c.openedAt || c.ts;
      if (!raw) return '—';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw).slice(0, 16);
      return d.toLocaleString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
    };
    const details = (c: Call) => [
      c.strategy ? `Strategy ${c.strategy}` : '',
      c.section ? `Desk ${c.section}` : '',
      c.ev != null ? `EV ${c.ev >= 0 ? '+' : '−'}$${Math.abs(c.ev) >= 1000 ? (Math.abs(c.ev) / 1000).toFixed(0) + 'k' : Math.abs(c.ev).toFixed(0)}` : '',
      c.dte != null ? `DTE ${Math.round(Number(c.dte))}` : '',
      c.relVol != null && c.relVol >= 1 ? `relVol ${Number(c.relVol).toFixed(1)}×` : '',
      c.horizon ? `Horizon ${String(c.horizon).slice(0, 10)}` : '',
      c.confidence != null ? `conf ${Math.round(Number(c.confidence))}${Number(c.confidence) <= 1 ? '' : '%'}` : '',
      c.rationale || '',
    ].filter(Boolean).join(' · ');
    const t = stats?.totals;
    const plStyle = (v?: number | null): React.CSSProperties => ({ color: v == null ? 'var(--text-muted)' : v >= 0 ? 'var(--up-text)' : 'var(--down-text)', fontWeight: 600 });
    const showActions = view === 'review' || view === 'held';
    return (
      <div className="mmec pcds" style={{ padding: '16px 16px 64px', display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--text)', minWidth: 0 }}>
        <div>
          <h1 className="heading-18" style={{ margin: 0 }}>Posted Calls</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '4px 0 0', maxWidth: 900 }}>One tracked call per name and direction. Repeat signals and cross-source duplicates are merged by the dedup gate; open calls are priced off the underlying for a live directional result.</p>
        </div>
        <div className="mmec-tabs" role="tablist">
          {([
            ['board', `Posted${calls.length ? ` · ${calls.length}` : ''}`],
            ['held', `Held${held.length ? ` · ${held.length}` : ''}`],
            ['performance', 'Performance'],
            ['review', `Review queue${pending.length ? ` · ${pending.length}` : ''}`],
          ] as const).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={view === k} className={`mmec-tab${view === k ? ' mmec-tab--on' : ''}`} style={{ flex: '0 0 auto' }} onClick={() => setView(k)}>{label}</button>
          ))}
        </div>
        {view === 'held' ? (
          <div style={{ padding: '8px 12px', background: 'var(--warning-wash)', border: '1px solid var(--warning)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', fontSize: 12 }}>
            {stats?.gate === 'trade_portal'
              ? 'For now only trade.modernmoneyexperts.com manual posts go to the board. Every other source is tracked privately here (for the backtest) — approve any you want to post it.'
              : 'Nothing auto-posts while results accumulate for the backtest. Every call is tracked privately here — approve any you want on the public board.'}
          </div>
        ) : null}
        {view === 'board' || view === 'held' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map(([v, label]) => (
              <button key={v} type="button" className={`mmec-pill mmec-pill--sm${statusFilter === v ? ' mmec-pill--active' : ''}`} onClick={() => setStatusFilter(v)}>{label}</button>
            ))}
            <span style={{ width: 1, height: 20, background: 'var(--line)' }} />
            <button type="button" className={`mmec-pill mmec-pill--sm${sourceFilter === '' ? ' mmec-pill--active' : ''}`} onClick={() => setSourceFilter('')}>All sources</button>
            {sourceChips.map((s) => (
              <button key={s.key} type="button" className={`mmec-pill mmec-pill--sm${sourceFilter === s.key ? ' mmec-pill--active' : ''}`} onClick={() => setSourceFilter(sourceFilter === s.key ? '' : s.key)}>{s.label}</button>
            ))}
            <input className="mmec-input" style={{ width: 180, marginInlineStart: 8 }} placeholder="Symbol…" value={symbol} onChange={(e) => setSymbol(e.target.value)} />
            {t ? (
              <span className="mmec-mono" style={{ marginInlineStart: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
                {t.calls} tracked · {t.closed} closed · win rate <span style={{ color: t.winRate == null ? 'var(--text-muted)' : t.winRate >= 50 ? 'var(--up-text)' : 'var(--down-text)' }}>{t.winRate == null ? '—' : `${t.winRate}%`}</span>
              </span>
            ) : null}
          </div>
        ) : null}

        {loading ? <ToolState kind="loading" />
          : error ? <ToolState kind="error" title={error} body="" onAction={() => { setLoading(true); load(); }} />
          : view === 'performance' ? (
            !stats ? <ToolState kind="empty" title="No stats yet." body="" /> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
                  {([
                    ['Tracked calls', String(stats.totals.calls), undefined],
                    ['Open', String(stats.totals.open), undefined],
                    ['Closed', String(stats.totals.closed), undefined],
                    ['Win rate (closed)', stats.totals.winRate == null ? '—' : `${stats.totals.winRate}%`, stats.totals.winRate == null ? undefined : stats.totals.winRate >= 50 ? 'var(--up-text)' : 'var(--down-text)'],
                  ] as const).map(([k, v, col]) => (
                    <div key={k} style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span className="mmec-label">{k}</span>
                      <span className="mmec-mono" style={{ fontSize: 20, fontWeight: 600, color: col }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', overflow: 'auto' }}>
                  <table className="mmec-table">
                    <thead><tr><th>Source</th><th style={{ textAlign: 'end' }}>Total</th><th style={{ textAlign: 'end' }}>Open</th><th style={{ textAlign: 'end' }}>Closed</th><th style={{ textAlign: 'end' }}>Wins</th><th style={{ textAlign: 'end' }}>Avg result</th></tr></thead>
                    <tbody>
                      {Object.entries(stats.bySource).map(([src, b]) => (
                        <tr key={src}>
                          <td>{src}</td>
                          <td className="mmec-num">{b.total}</td>
                          <td className="mmec-num">{b.open}</td>
                          <td className="mmec-num">{b.closed}</td>
                          <td className="mmec-num">{b.wins}</td>
                          <td className="mmec-num" style={plStyle(b.avgResult)}>{b.avgResult == null ? '—' : pct(b.avgResult)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )
          )
          : list.length === 0 ? <ToolState kind="empty" title={view === 'review' ? 'Nothing waiting for review.' : view === 'board' ? 'Nothing posted yet — calls are held while the gate is backtested. Approve from the Held tab to post.' : 'No calls match these filters.'} body="" />
          : (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)' }}>
              <table className="mmec-table">
                <thead><tr>
                  <th>Posted</th><th>Contract</th><th>Side</th>
                  <th style={{ textAlign: 'end' }}>Entry</th><th style={{ textAlign: 'end' }}>Target</th><th style={{ textAlign: 'end' }}>Stop</th>
                  <th style={{ textAlign: 'end' }}>Last</th><th style={{ textAlign: 'end' }}>P/L</th><th>Status</th><th>Source</th>
                  {showActions ? <th /> : null}
                </tr></thead>
                <tbody>
                  {list.map((c) => {
                    const up = c.direction === 'bullish';
                    const tone = STATUS_TONE[c.status];
                    const info = details(c);
                    return (
                      <tr key={c.id} title={info || undefined}>
                        <td className="mmec-mono" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{postedAt(c)}</td>
                        <td className="mmec-mono" style={{ whiteSpace: 'nowrap' }}>{c.underlying}{c.contract ? <span style={{ color: 'var(--text-muted)' }}> · {c.contract}</span> : null}</td>
                        <td><span style={{ fontWeight: 600, color: up ? 'var(--up-text)' : 'var(--down-text)' }}>{up ? '▲ Bullish' : '▼ Bearish'}</span></td>
                        <td className="mmec-num">{money(c.entry)}</td>
                        <td className="mmec-num mmec-num--up">{c.target || '—'}</td>
                        <td className="mmec-num mmec-num--down">{c.stop || '—'}</td>
                        <td className="mmec-num">{money(c.current)}</td>
                        <td className="mmec-num" style={plStyle(c.pnlPct)}>{c.pnlPct == null ? '—' : pct(c.pnlPct)}<span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 10, marginInlineStart: 4 }}>{c.status === 'OPEN' ? 'unrl' : ''}</span></td>
                        <td><span className={`mmec-tag${tone ? ` mmec-tag--${tone}` : ''}`}>{(c.status || '').replace('_', ' ')}</span></td>
                        <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{c.sources.length ? c.sources.join(', ') : c.sourceLabel}{c.seenCount > 1 ? <span className="mmec-mono"> ×{c.seenCount}</span> : null}</td>
                        {showActions ? (
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <button type="button" className="mmec-btn" style={{ height: 26 }} disabled={busyId === c.id} onClick={() => moderate(c.id, 'approve')}>Approve</button>{' '}
                            <button type="button" className="mmec-btn mmec-btn--ghost" style={{ height: 26 }} disabled={busyId === c.id} onClick={() => moderate(c.id, 'reject')}>Reject</button>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <h1 style={S.h1}>Posted Calls</h1>
      <p style={S.sub}>One tracked call per name and direction. Repeat signals and cross-source duplicates are merged by the dedup gate; open calls are priced off the underlying for a live directional result.</p>

      <div style={S.seg}>
        <button style={S.segBtn(view === 'board')} onClick={() => setView('board')}>Posted{calls.length ? ` · ${calls.length}` : ''}</button>
        <button style={S.segBtn(view === 'held')} onClick={() => setView('held')}>Held{held.length ? ` · ${held.length}` : ''}</button>
        <button style={S.segBtn(view === 'performance')} onClick={() => setView('performance')}>Performance</button>
        <button style={S.segBtn(view === 'review')} onClick={() => setView('review')}>Review queue{pending.length ? ` · ${pending.length}` : ''}</button>
      </div>
      {view === 'held' ? (
        <div style={{ margin: '12px 0 0', padding: '9px 13px', background: 'rgba(244,162,97,0.1)', border: '1px solid rgba(244,162,97,0.3)', borderRadius: 8, color: '#f0b483', fontSize: 12.5 }}>
          {stats?.gate === 'trade_portal'
            ? 'For now only trade.modernmoneyexperts.com manual posts go to the board. Every other source is tracked privately here (for the backtest) — approve any you want to post it.'
            : 'Nothing auto-posts while results accumulate for the backtest. Every call is tracked privately here — approve any you want on the public board.'}
        </div>
      ) : null}

      {view === 'board' || view === 'held' ? (
        <>
          <div style={S.chipRow}>
            <button style={S.chip(sourceFilter === '')} onClick={() => setSourceFilter('')}>All sources</button>
            {sourceChips.map((s) => <button key={s.key} style={S.chip(sourceFilter === s.key, s.color)} onClick={() => setSourceFilter(sourceFilter === s.key ? '' : s.key)}>{s.label}</button>)}
          </div>
          <div style={S.chipRow}>
            {STATUS_FILTERS.map(([v, label]) => <button key={v} style={S.chip(statusFilter === v)} onClick={() => setStatusFilter(v)}>{label}</button>)}
            <input style={S.input} placeholder="Symbol…" value={symbol} onChange={(e) => setSymbol(e.target.value)} />
          </div>
        </>
      ) : null}

      <div style={{ marginTop: 14 }}>
        {loading ? <div style={S.empty}>Loading…</div>
          : error ? <div style={{ ...S.empty, color: '#ef7a73' }}>{error}</div>
          : view === 'performance' ? renderPerformance()
          : list.length === 0 ? <div style={S.empty}>{view === 'review' ? 'Nothing waiting for review.' : view === 'board' ? 'Nothing posted yet — calls are held while the gate is backtested. Approve from the Held tab to post.' : 'No calls match these filters.'}</div>
          : list.map(renderCard)}
      </div>
    </div>
  );
};

export default PostedCallsFeed;
